/**
 * Almora native cart drawer
 * - Re-renders the drawer with the Section Rendering API after every cart change
 * - Keeps free gift tiers (theme settings → Cart Free Gifts) in sync: adds / swaps / removes gift lines
 * - Line quantity, remove, variant swap, "Frequently Bought Together" quick add
 *
 * Gift lines carry the hidden line property `_free_gift` so they can be told apart from
 * the same product bought normally. BOGO lines (`_bogo`) are handled by cart.js.
 */
(function () {
  if (window.AlmoraCartDrawer) return;

  let ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  let GIFT_PROP = "_free_gift";

  let state = {
    running: false,
    again: false,
    selfMutating: 0,
    lastCart: null,
    failedGifts: new Set(),
    debounce: 0,
    qtyTimers: new Map(),
  };

  // ---------- helpers ----------
  function drawer() {
    return document.querySelector("[data-cart-drawer]");
  }
  function sectionId() {
    let d = drawer();
    return d ? d.dataset.id : null;
  }
  function popoverSupported() {
    return typeof HTMLElement.prototype.showPopover === "function";
  }
  // ---------- currency ----------
  // Theme settings amounts are in the store currency. Shopify.currency.rate converts them to the
  // shopper's currency; amounts are rounded up to a whole unit, same as sections/mini-cart.liquid.
  // Gift tiers: fixed per-currency amounts come pre-computed (threshold_fixed); converted ones are
  // rounded to the nearest amount ending in 5, same as snippets/cart-gift-threshold.liquid.
  function fxRate(shopCurrency) {
    let cur = window.Shopify && Shopify.currency;
    if (!cur || !cur.active || !shopCurrency || cur.active === shopCurrency) return 1;
    let rate = parseFloat(cur.rate);
    return rate > 0 ? rate : 1;
  }
  function toCents(units, rate) {
    return Math.ceil((Number(units) || 0) * rate - 0.000001) * 100;
  }
  function giftCents(units, rate) {
    let raw = (Number(units) || 0) * rate;
    if (!(raw > 0)) return 0;
    return Math.max(5, Math.round((raw - 5) / 10) * 10 + 5) * 100;
  }
  function shopCurrency() {
    let el = document.getElementById("cart-gift-config");
    try { return el ? JSON.parse(el.textContent).shop_currency : null; } catch (e) { return null; }
  }
  /** Stores the live rate on the cart (attribute "_fx") so Liquid can convert. Returns true when changed. */
  async function syncCurrency(cart) {
    let cur = window.Shopify && Shopify.currency;
    let base = shopCurrency();
    if (!cur || !cur.active || !base || cur.active === base || !(parseFloat(cur.rate) > 0)) return false;
    if (cart.currency && cart.currency !== cur.active) return false;
    let wanted = cur.active + ":" + cur.rate;
    let attrs = cart.attributes || {};
    if (attrs._fx === wanted) return false;
    let res = await postJSON("cart/update.js", { attributes: { _fx: wanted } });
    return !!res.ok;
  }

  function giftConfig() {
    let el = document.getElementById("cart-gift-config");
    if (!el) return { enabled: false, tiers: [] };
    try {
      let cfg = JSON.parse(el.textContent);
      let rate = fxRate(cfg.shop_currency);
      (cfg.tiers || []).forEach(function (t) {
        if (!t) return;
        if (t.threshold_fixed > 0) t.threshold = t.threshold_fixed;
        else if (t.threshold_base != null) t.threshold = giftCents(t.threshold_base, rate);
      });
      cfg.tiers = (cfg.tiers || [])
        .filter(function (t) { return t && t.variant_id && t.threshold > 0; })
        .sort(function (a, b) { return a.threshold - b.threshold; });
      return cfg;
    } catch (e) {
      return { enabled: false, tiers: [] };
    }
  }
  function isGift(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }
  function postJSON(path, body) {
    state.selfMutating += 1;
    return fetch(ROOT + path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r
          .json()
          .catch(function () { return {}; })
          .then(function (data) { return { ok: r.ok, data: data }; });
      })
      .finally(function () {
        state.selfMutating -= 1;
      });
  }
  function getCart() {
    return fetch(ROOT + "cart.js", { credentials: "same-origin", cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        state.lastCart = cart;
        return cart;
      });
  }
  function onCartPage() {
    return document.body.classList.contains("template-cart");
  }

  // ---------- free gifts ----------
  function eligibleAmount(cart, cfg) {
    let giftValue = 0;
    cart.items.forEach(function (item) {
      if (isGift(item)) giftValue += item.final_line_price || 0;
    });
    let base = cfg.basis === "subtotal" ? cart.items_subtotal_price : cart.total_price;
    return base - giftValue;
  }

  function desiredGifts(cart, cfg) {
    if (!cfg.enabled || !cfg.tiers.length) return [];
    let paidQty = cart.items.reduce(function (sum, item) {
      return isGift(item) ? sum : sum + item.quantity;
    }, 0);
    if (paidQty < 1) return [];
    let amount = eligibleAmount(cart, cfg);
    let unlocked = cfg.tiers.filter(function (t) {
      return t.available !== false && amount >= t.threshold && !state.failedGifts.has(String(t.variant_id));
    });
    if (cfg.mode === "stack") return unlocked;
    return unlocked.length ? [unlocked[unlocked.length - 1]] : [];
  }

  /** Returns true when the cart was changed. */
  async function syncGifts(cart) {
    let cfg = giftConfig();
    let desired = desiredGifts(cart, cfg);
    let giftLines = cart.items.filter(isGift);
    let kept = new Set();
    let updates = {};
    let hasUpdates = false;

    // Gift products added without the gift property (product page, direct link, another app)
    // are removed: they can only be in the cart as a tier gift.
    if (cfg.block_purchase) {
      let giftProducts = new Set((cfg.tiers || []).map(function (t) { return String(t.product_id); }));
      cart.items.forEach(function (item) {
        if (!isGift(item) && giftProducts.has(String(item.product_id))) {
          updates[item.key] = 0;
          hasUpdates = true;
        }
      });
    }

    giftLines.forEach(function (line) {
      let vid = String(line.variant_id);
      let wanted = desired.some(function (t) { return String(t.variant_id) === vid; });
      if (!wanted || kept.has(vid)) {
        updates[line.key] = 0;
        hasUpdates = true;
      } else {
        kept.add(vid);
        if (line.quantity !== 1) {
          updates[line.key] = 1;
          hasUpdates = true;
        }
      }
    });

    let toAdd = desired.filter(function (t) { return !kept.has(String(t.variant_id)); });
    if (!hasUpdates && !toAdd.length) return false;

    if (hasUpdates) await postJSON("cart/update.js", { updates: updates });

    for (let i = 0; i < toAdd.length; i++) {
      let t = toAdd[i];
      let res = await postJSON("cart/add.js", {
        items: [{ id: Number(t.variant_id), quantity: 1, properties: { _free_gift: t.label || "Free gift" } }],
      });
      // Out of stock / unpublished gift: stop retrying for this page view.
      if (!res.ok) state.failedGifts.add(String(t.variant_id));
    }
    return true;
  }

  // ---------- rendering ----------
  function updateCounts(paidCount) {
    document.querySelectorAll(".js-cart-count").forEach(function (el) {
      el.textContent = paidCount;
    });
    document.querySelectorAll(".js-cart").forEach(function (el) {
      el.classList.toggle("has-items", paidCount > 0);
      el.classList.toggle("empty", paidCount <= 0);
    });
  }

  function readDrawerState(scope) {
    let el = scope && scope.querySelector("[data-cart-drawer-state]");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  async function render() {
    let id = sectionId();
    let d = drawer();
    if (!id || !d) {
      if (state.lastCart) {
        updateCounts(state.lastCart.items.reduce(function (s, i) { return isGift(i) ? s : s + i.quantity; }, 0));
      }
      return;
    }
    let res = await fetch(ROOT + "?sections=" + encodeURIComponent(id), { credentials: "same-origin", cache: "no-store" });
    let json = await res.json();
    let html = json[id];
    if (!html) return;
    let doc = new DOMParser().parseFromString(html, "text/html");
    let fresh = doc.querySelector("[data-cart-drawer-inner]");
    let current = d.querySelector("[data-cart-drawer-inner]");
    if (!fresh || !current) return;

    // Preserve UI state across the swap
    let body = current.querySelector("[data-cart-drawer-body]");
    let scrollTop = body ? body.scrollTop : 0;
    let summaryToggle = current.querySelector("[data-summary-toggle]");
    let summaryOpen = !!(summaryToggle && summaryToggle.getAttribute("aria-expanded") === "true");
    let track = current.querySelector("[data-upsell-track]");
    let trackScroll = track ? track.scrollLeft : 0;
    let focusedKey = null;
    let focusedSel = null;
    if (document.activeElement && current.contains(document.activeElement)) {
      let line = document.activeElement.closest("[data-cart-line]");
      focusedKey = line ? line.dataset.key : null;
      if (document.activeElement.matches("[data-qty-step='1']")) focusedSel = "[data-qty-step='1']";
      else if (document.activeElement.matches("[data-qty-step='-1']")) focusedSel = "[data-qty-step='-1']";
      else if (document.activeElement.matches("[data-qty-input]")) focusedSel = "[data-qty-input]";
    }

    current.replaceWith(fresh);

    let newBody = fresh.querySelector("[data-cart-drawer-body]");
    if (newBody) newBody.scrollTop = scrollTop;
    if (summaryOpen) setSummary(fresh, true);
    let newTrack = fresh.querySelector("[data-upsell-track]");
    if (newTrack) newTrack.scrollLeft = trackScroll;
    if (focusedKey && focusedSel) {
      let target = fresh.querySelector('[data-cart-line][data-key="' + CSS.escape(focusedKey) + '"] ' + focusedSel);
      if (target) target.focus({ preventScroll: true });
    }

    arrangeUpsell();
    if (newTrack) newTrack.scrollLeft = trackScroll;
    let st = readDrawerState(fresh);
    if (st) updateCounts(st.paid_count);
    updateUpsellNav();
  }

  async function refresh(options) {
    options = options || {};
    if (state.running) {
      state.again = true;
      return;
    }
    state.running = true;
    setLoading(true);
    try {
      let guard = 0;
      do {
        state.again = false;
        let cart = await getCart();
        let fxChanged = await syncCurrency(cart).catch(function () { return false; });
        if (fxChanged) cart = await getCart();
        let changed = await syncGifts(cart).catch(function () { return false; });
        changed = changed || fxChanged;
        if (changed) await getCart();
        if (changed || !options.onlyIfChanged) await render();
        if (changed && onCartPage() && typeof ajaxify !== "undefined") {
          try { ajaxify.onCartUpdated(); } catch (e) {}
        }
        options.onlyIfChanged = false;
        guard += 1;
      } while (state.again && guard < 4);
    } catch (e) {
      console.error("[cart-drawer] refresh failed", e);
    } finally {
      state.running = false;
      setLoading(false);
    }
  }

  function scheduleRefresh() {
    if (state.selfMutating > 0) return; // our own requests refresh explicitly
    clearTimeout(state.debounce);
    state.debounce = setTimeout(function () { refresh(); }, 120);
  }

  function setLoading(on) {
    let d = drawer();
    if (d) d.classList.toggle("is-loading", !!on);
  }

  function showError(message) {
    let body = drawer() && drawer().querySelector("[data-cart-drawer-body]");
    if (!body) return;
    let el = body.querySelector(".cart-drawer__error");
    if (!el) {
      el = document.createElement("p");
      el.className = "cart-drawer__error";
      el.setAttribute("role", "alert");
      body.prepend(el);
    }
    el.textContent = message || "Something went wrong. Please try again.";
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.remove(); }, 4500);
  }

  // ---------- open / close ----------
  function open(options) {
    let d = drawer();
    if (d && options && options.loading) {
      setLoading(true);
      clearTimeout(state.loadingTimer);
      state.loadingTimer = setTimeout(function () { if (!state.running) setLoading(false); }, 4000);
    }
    if (!d) {
      window.location.href = ROOT + "cart";
      return;
    }
    document.querySelectorAll("form.quick-add--popover[popover]").forEach(function (p) {
      try { if (p.matches(":popover-open")) p.hidePopover(); } catch (e) {}
    });
    if (popoverSupported()) {
      try { if (!d.matches(":popover-open")) d.showPopover(); } catch (e) {}
    } else {
      document.documentElement.classList.add("mini-cart-open");
      d.classList.add("open");
    }
  }
  function close() {
    let d = drawer();
    if (!d) return;
    if (popoverSupported()) {
      try { if (d.matches(":popover-open")) d.hidePopover(); } catch (e) {}
    }
    document.documentElement.classList.remove("mini-cart-open");
    d.classList.remove("open");
  }

  // ---------- line actions ----------
  async function changeLine(key, quantity) {
    setLoading(true);
    let res = await postJSON("cart/change.js", { id: key, quantity: quantity });
    await refresh();
    if (!res.ok) showError(res.data && (res.data.description || res.data.message));
  }

  async function swapVariant(key, newVariantId) {
    let cart = state.lastCart || (await getCart());
    let line = cart.items.find(function (i) { return i.key === key; });
    if (!line || String(line.variant_id) === String(newVariantId)) return;
    setLoading(true);
    let props = line.properties || {};
    let removed = await postJSON("cart/change.js", { id: key, quantity: 0 });
    if (!removed.ok) {
      showError(removed.data && removed.data.description);
      return refresh();
    }
    let added = await postJSON("cart/add.js", {
      items: [{ id: Number(newVariantId), quantity: line.quantity, properties: props }],
    });
    if (!added.ok) {
      // Put the original line back
      await postJSON("cart/add.js", { items: [{ id: line.variant_id, quantity: line.quantity, properties: props }] });
    }
    await refresh();
    if (!added.ok) showError(added.data && (added.data.description || "That option is unavailable."));
  }

  async function addUpsell(variantId, button) {
    if (button) button.classList.add("is-adding");
    let res = await postJSON("cart/add.js", { items: [{ id: Number(variantId), quantity: 1 }] });
    await refresh();
    if (!res.ok) showError(res.data && (res.data.description || res.data.message));
  }

  function queueQty(lineEl, qty) { 
    let key = lineEl.dataset.key;
    let input = lineEl.querySelector("[data-qty-input]");
    let max = input && input.max ? parseInt(input.max, 10) : Infinity;
    qty = Math.max(0, Math.min(qty, isFinite(max) ? max : qty));
    if (input) input.value = qty;
    clearTimeout(state.qtyTimers.get(key));
    state.qtyTimers.set(
      key,
      setTimeout(function () {
        state.qtyTimers.delete(key);
        if (String(qty) !== lineEl.dataset.quantity) changeLine(key, qty);
      }, 350)
    );
  }

  // ---------- order summary (expands above the total) ----------
  function setSummary(scope, open) {
    let toggle = scope && scope.querySelector("[data-summary-toggle]");
    let panel = scope && scope.querySelector("[data-summary-panel]");
    if (!toggle || !panel) return;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    panel.hidden = !open;
    let footer = toggle.closest("[data-cart-summary]");
    if (footer) footer.classList.toggle("is-open", !!open);
  }

  // ---------- upsell carousel ----------
  function updateUpsellNav() {
    let d = drawer();
    if (!d) return;
    let track = d.querySelector("[data-upsell-track]");
    if (!track) return;
    let prev = d.querySelector("[data-upsell-prev]");
    let next = d.querySelector("[data-upsell-next]");
    let max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  // ---------- FBT ordering by next-reward gap ----------
  function setCardVariant(card, option) {
    if (!option) return;
    let price = card.querySelector("[data-upsell-price]");
    let compare = card.querySelector("[data-upsell-compare]");
    let off = card.querySelector("[data-upsell-off]");
    if (price && option.dataset.money) price.textContent = option.dataset.money;
    let hasCompare = !!option.dataset.compare;
    if (compare) { compare.textContent = option.dataset.compare || ""; compare.hidden = !hasCompare; }
    if (off) { off.textContent = option.dataset.off || ""; off.hidden = !hasCompare; }
  }

  function arrangeUpsell() {
    let d = drawer();
    let up = d && d.querySelector("[data-cart-upsell]");
    let track = up && up.querySelector("[data-upsell-track]");
    if (!track) return;

    let gap = parseInt(up.dataset.gap, 10) || 0;
    let ratio = parseFloat(up.dataset.ratio);
    if (!(ratio > 0)) ratio = 1;
    let limit = parseInt(up.dataset.limit, 10) || 6;
    let useGap = up.dataset.match === "true" && gap > 0;
    let prefer = up.dataset.prefer !== "false";
    let unlockTpl = up.dataset.unlockText || "";
    let label = up.dataset.label || "";

    let cards = Array.prototype.slice.call(track.querySelectorAll("[data-upsell-card]"));
    let ranked = cards.map(function (card) {
      let index = parseInt(card.dataset.index, 10) || 0;
      let select = card.querySelector("[data-upsell-variant]");
      let button = card.querySelector("[data-upsell-add]");
      let options = select
        ? Array.prototype.slice.call(select.options).map(function (o) { return { el: o, price: parseInt(o.dataset.price, 10) || 0 }; })
        : [{ el: null, price: parseInt(button && button.dataset.price, 10) || 0 }];

      let best = null;
      if (useGap) {
        options.forEach(function (o) {
          let effective = Math.round(o.price * ratio);
          let covers = effective >= gap;
          let score = covers ? effective - gap : gap - effective + (prefer ? 1e9 : 0);
          if (!best || score < best.score) best = { option: o, score: score, covers: covers };
        });
        if (select && best && best.option.el && select.value !== best.option.el.value) {
          select.value = best.option.el.value;
          setCardVariant(card, best.option.el);
        }
      }

      let tag = card.querySelector("[data-upsell-unlock]");
      if (tag) {
        let show = useGap && best && best.covers && unlockTpl;
        tag.hidden = !show;
        let text = tag.querySelector("[data-upsell-unlock-text]");
        if (text) text.textContent = show ? unlockTpl.replace("[gift]", label) : "";
      }
      return { card: card, index: index, score: useGap && best ? best.score : index };
    });

    ranked.sort(function (a, b) { return a.score - b.score || a.index - b.index; });
    ranked.forEach(function (r, i) {
      track.appendChild(r.card);
      r.card.hidden = i >= limit;
    });

    let visible = Math.min(ranked.length, limit);
    up.hidden = visible === 0;
    let nav = up.querySelector(".cart-upsell__nav");
    if (nav) nav.hidden = visible < 2;
    track.scrollLeft = 0;
    updateUpsellNav();
  }

  // ---------- events (delegated, survive re-renders) ----------
  document.addEventListener("click", function (e) {
    let d = drawer();
    if (!d || !d.contains(e.target)) return;

    let step = e.target.closest("[data-qty-step]");
    if (step) {
      e.preventDefault();
      let lineEl = step.closest("[data-cart-line]");
      let input = lineEl.querySelector("[data-qty-input]");
      queueQty(lineEl, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep, 10));
      return;
    }

    let remove = e.target.closest("[data-cart-remove]");
    if (remove) {
      e.preventDefault();
      let rl = remove.closest("[data-cart-line]");
      rl.classList.add("is-removing");
      changeLine(rl.dataset.key, 0);
      return;
    }

    let add = e.target.closest("[data-upsell-add]");
    if (add) {
      e.preventDefault();
      let card = add.closest(".cart-upsell__card");
      let select = card && card.querySelector("[data-upsell-variant]");
      addUpsell(select ? select.value : add.dataset.variantId, add);
      return;
    }

    let summaryBtn = e.target.closest("[data-summary-toggle]");
    if (summaryBtn) {
      e.preventDefault();
      setSummary(d, summaryBtn.getAttribute("aria-expanded") !== "true");
      return;
    }

    let nav = e.target.closest("[data-upsell-prev], [data-upsell-next]");
    if (nav) {
      let track = d.querySelector("[data-upsell-track]");
      let cardEl = track && track.querySelector(".cart-upsell__card");
      let amount = cardEl ? cardEl.getBoundingClientRect().width + 12 : 240;
      track.scrollBy({ left: nav.hasAttribute("data-upsell-prev") ? -amount : amount, behavior: "smooth" });
      return;
    }

    if (e.target.closest("[data-cart-close]") && !popoverSupported()) {
      close();
    }
  });

  document.addEventListener("change", function (e) {
    let d = drawer();
    if (!d || !d.contains(e.target)) return;
    if (e.target.matches("[data-qty-input]")) {
      queueQty(e.target.closest("[data-cart-line]"), parseInt(e.target.value, 10) || 0);
    } else if (e.target.matches("[data-upsell-variant]")) {
      setCardVariant(e.target.closest("[data-upsell-card]"), e.target.selectedOptions[0]);
    } else if (e.target.matches("[data-cart-variant]")) {
      swapVariant(e.target.closest("[data-cart-line]").dataset.key, e.target.value);
    }
  });

  document.addEventListener(
    "scroll",
    function (e) {
      if (e.target && e.target.matches && e.target.matches("[data-upsell-track]")) updateUpsellNav();
    },
    true
  );

  // Carousel arrows can only be measured once the drawer is visible
  document.addEventListener(
    "toggle",
    function (e) {
      if (e.target === drawer() && e.newState === "open") requestAnimationFrame(updateUpsellNav);
    },
    true
  );

  // Any cart mutation elsewhere on the site (cart.js dispatches this after add/change/update)
  document.addEventListener("almora:cart-updated", scheduleRefresh);
  document.addEventListener("cart:refresh", scheduleRefresh);

  // Re-bind after the drawer is reloaded in the theme editor
  document.addEventListener("shopify:section:load", function (e) {
    if (drawer() && e.target.contains(drawer())) {
      arrangeUpsell();
    }
  });

  function init() {
    arrangeUpsell();
    let st = readDrawerState(document);
    if (st) updateCounts(st.paid_count);
    // Bring gifts in line with the current cart (e.g. after a discount or an app changed it)
    let run = function () { refresh({ onlyIfChanged: true }); };
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 800);
  }

  window.AlmoraCartDrawer = { open: open, close: close, refresh: refresh };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

/**
 * Free gift products: add-to-cart stays disabled.
 * Forms rendered with data-free-gift="true" (snippets/form-add-to-cart.liquid) are blocked from
 * submitting, and their button is re-locked whenever other scripts (variant / stock logic in
 * cart.js) try to enable it.
 */
(function () {
  let FORM_SEL = 'form[data-free-gift="true"]';

  function lock(btn) {
    if (!btn) return;
    if (!btn.hasAttribute("disabled")) btn.setAttribute("disabled", "");
    if (btn.getAttribute("aria-disabled") !== "true") btn.setAttribute("aria-disabled", "true");
    if (!btn.classList.contains("disabled--button")) btn.classList.add("disabled--button");
    let label = btn.getAttribute("data-disabled");
    let text = btn.querySelector(".disabled-text");
    if (text && label && label !== "true" && text.textContent.trim() !== label) text.textContent = label;
  }

  function lockAll(scope) {
    (scope || document).querySelectorAll(FORM_SEL).forEach(function (form) {
      form.querySelectorAll('button[name="add"], .add-to-cart-button').forEach(function (btn) {
        lock(btn);
        if (btn.__giftLockObserved) return;
        btn.__giftLockObserved = true;
        new MutationObserver(function () { lock(btn); })
          .observe(btn, { attributes: true, attributeFilter: ["disabled", "class"], childList: true, subtree: true });
      });
    });
  }

  function block(e) {
    let form = e.target && e.target.closest && e.target.closest(FORM_SEL);
    if (!form) return;
    if (e.type === "click" && !e.target.closest('button[name="add"], .add-to-cart-button, [type="submit"]')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }
  document.addEventListener("submit", block, true);
  document.addEventListener("click", block, true);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { lockAll(); });
  else lockAll();
  document.addEventListener("shopify:section:load", function (e) { lockAll(e.target); });
})();